import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Send, Users, Smartphone, Monitor, AlertCircle, CheckCircle2, Loader2, History, Clock, Megaphone, Sparkles, CalendarClock, HandHeart, Calendar, Timer, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/api/client';

// Short list of pre-written broadcasts the admin can tap to pre-fill the form.
// Each tap just fills the inputs; the admin still reviews before sending.
const QUICK_TEMPLATES = [
  {
    label: 'Live service starting',
    title: '🙏 Live service is starting',
    body: 'Join us now for Sunday worship — tap to open the livestream.',
    url: '/live',
  },
  {
    label: 'New leaflet',
    title: '📖 A new gospel leaflet is out',
    body: 'Tap to open the Reading Room and flip through the latest leaflet.',
    url: '/leaflets',
  },
  {
    label: 'Morning Prayer reminder',
    title: '🌅 Morning Prayer in 10 minutes',
    body: "Join us for prayer — we'd love to pray with you today.",
    url: '/morning-prayer',
  },
  {
    label: 'Event reminder',
    title: '📅 Event reminder',
    body: 'Our next event is coming up — see details and RSVP.',
    url: '/',
  },
];


// Format a Date into the shape the <input type="datetime-local"> expects:
// "YYYY-MM-DDTHH:mm" in LOCAL time (no timezone suffix). We feed this to the
// min= attribute and as the initial value.
function toLocalInputValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) +
    'T' + pad(date.getHours()) + ':' + pad(date.getMinutes())
  );
}

// Default schedule picker value: 2 hours from now, rounded up to the next
// quarter hour, so the admin sees something sensible on first toggle.
function defaultScheduleLocal() {
  const d = new Date(Date.now() + 2 * 60 * 60 * 1000);
  const minutes = d.getMinutes();
  const rounded = Math.ceil(minutes / 15) * 15;
  d.setMinutes(rounded === 60 ? 0 : rounded, 0, 0);
  if (rounded === 60) d.setHours(d.getHours() + 1);
  return toLocalInputValue(d);
}

// Min attr on the datetime input — "now" so browsers don't allow past dates.
function minScheduleLocal() {
  return toLocalInputValue(new Date());
}


export default function PushBroadcastTab() {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/');
  // Scheduling: when `scheduleMode = true` we POST to /scheduled instead of
  // /broadcast; the datetime-local input value is a naive local-time string
  // (no timezone), which we convert to an ISO UTC string before sending.
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledAtLocal, setScheduledAtLocal] = useState(() => defaultScheduleLocal());
  const [confirming, setConfirming] = useState(false);

  const { data: stats, isLoading: statsLoading, isError: statsError } = useQuery({
    queryKey: ['adm-push-stats'],
    queryFn: async () => (await api.get('/admin/push/stats')).data,
    refetchInterval: 30_000,
  });

  // Last 20 push events (manual + welcome + scheduled, newest first).
  // We refetch after every broadcast so the audit list stays live.
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['adm-push-history'],
    queryFn: async () => (await api.get('/admin/push/history?limit=20')).data,
    refetchInterval: 60_000,
  });

  // Scheduled drafts awaiting their send window + recently sent/cancelled.
  const { data: draftsData } = useQuery({
    queryKey: ['adm-push-scheduled'],
    queryFn: async () => (await api.get('/admin/push/scheduled')).data,
    refetchInterval: 60_000,
  });
  const pendingDrafts = (draftsData?.items || []).filter((d) => d.status === 'pending');

  const scheduleMut = useMutation({
    mutationFn: async () => {
      const scheduled = new Date(scheduledAtLocal); // local → ISO
      return (await api.post('/admin/push/scheduled', {
        title, body, url,
        scheduled_at: scheduled.toISOString(),
      })).data;
    },
    onSuccess: () => {
      toast.success(`Scheduled for ${new Date(scheduledAtLocal).toLocaleString('en-IE', { dateStyle: 'medium', timeStyle: 'short' })}.`);
      setTitle(''); setBody(''); setUrl('/');
      setScheduleMode(false);
      setScheduledAtLocal(defaultScheduleLocal());
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ['adm-push-scheduled'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || 'Could not schedule push.');
      setConfirming(false);
    },
  });

  const cancelDraft = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/push/scheduled/${id}`)).data,
    onSuccess: () => {
      toast.success('Scheduled push cancelled.');
      qc.invalidateQueries({ queryKey: ['adm-push-scheduled'] });
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || 'Could not cancel.'),
  });

  const broadcast = useMutation({
    mutationFn: async () => (await api.post('/admin/push/broadcast', { title, body, url })).data,
    onSuccess: (data) => {
      toast.success(
        `Sent to ${data.sent} of ${data.attempted} device${data.attempted === 1 ? '' : 's'}` +
        (data.staleRemoved ? ` · cleaned up ${data.staleRemoved} stale subscription${data.staleRemoved === 1 ? '' : 's'}` : ''),
      );
      setTitle('');
      setBody('');
      setUrl('/');
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ['adm-push-stats'] });
      qc.invalidateQueries({ queryKey: ['adm-push-history'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || 'Could not send broadcast.');
      setConfirming(false);
    },
  });

  const trimmedTitle = title.trim();
  const trimmedBody = body.trim();
  const scheduleFutureEnough = scheduleMode
    ? (new Date(scheduledAtLocal).getTime() - Date.now() > 60_000) // at least 1 min ahead
    : true;
  const canSend = trimmedTitle.length > 0 && trimmedBody.length > 0 && scheduleFutureEnough
    && !broadcast.isPending && !scheduleMut.isPending;

  const applyTemplate = (t) => {
    setTitle(t.title);
    setBody(t.body);
    setUrl(t.url);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSend) return;
    if (scheduleMode) {
      // No confirm dialog for scheduled sends — admin picked a future time
      // already, so one tap is enough. They can still cancel from the
      // "Scheduled drafts" list below.
      scheduleMut.mutate();
      return;
    }
    setConfirming(true);
  };

  const confirmSend = () => broadcast.mutate();

  // ---- Render ----
  return (
    <div className="space-y-6" data-testid="push-broadcast-tab">
      {/* Overview */}
      <div className="card-moj p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-serif text-xl text-moj-purple-deep flex items-center gap-2">
              <Bell size={20} className="text-moj-gold-deep" /> Push Broadcasts
            </h2>
            <p className="text-sm text-moj-charcoal/80 mt-1 max-w-2xl">
              Send a single notification to every member who has enabled notifications.
              Keep messages short, warm and pastoral — people see them on their lock screen.
            </p>
          </div>
          {statsError && (
            <div className="text-xs text-red-600 inline-flex items-center gap-1" data-testid="push-stats-error">
              <AlertCircle size={14} /> Could not load stats
            </div>
          )}
        </div>

        {/* Audience summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
          <StatCard
            icon={<Users size={18} />}
            label="Total subscribers"
            value={statsLoading ? '…' : stats?.total ?? 0}
            testid="stat-total"
          />
          <StatCard
            icon={<Smartphone size={18} />}
            label="Mobile (iOS + Android)"
            value={statsLoading ? '…' : (stats?.byPlatform?.ios || 0) + (stats?.byPlatform?.android || 0)}
            testid="stat-mobile"
          />
          <StatCard
            icon={<Monitor size={18} />}
            label="Desktop / other"
            value={statsLoading ? '…' : (stats?.byPlatform?.desktop || 0) + (stats?.byPlatform?.unknown || 0)}
            testid="stat-desktop"
          />
        </div>

        {stats && stats.configured === false && (
          <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-start gap-2" data-testid="push-not-configured">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0"/>
            <span>Push is not fully configured on the server (missing VAPID env vars). Contact your developer.</span>
          </div>
        )}
      </div>

      {/* Compose */}
      <form onSubmit={handleSubmit} className="card-moj p-5 space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="font-serif text-lg text-moj-purple-deep">Compose</h3>
          <div className="flex flex-wrap gap-1.5">
            {QUICK_TEMPLATES.map((t) => (
              <button
                key={t.label}
                type="button"
                onClick={() => applyTemplate(t)}
                className="text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-moj-cream border border-moj-gold/40 text-moj-purple-deep hover:bg-moj-gold/15 transition touch-manipulation"
                data-testid={`push-template-${t.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="push-title" className="block text-xs font-semibold uppercase tracking-wide text-moj-charcoal/70 mb-1">
            Title <span className="font-normal normal-case">({title.length}/60 ideal, 120 max)</span>
          </label>
          <input
            id="push-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            required
            placeholder="e.g. 🙏 Live service is starting"
            className="w-full px-3 py-2.5 rounded-lg border border-moj-gold/40 bg-white focus:outline-none focus:ring-2 focus:ring-moj-gold focus:border-moj-gold"
            data-testid="push-title-input"
          />
        </div>

        <div>
          <label htmlFor="push-body" className="block text-xs font-semibold uppercase tracking-wide text-moj-charcoal/70 mb-1">
            Message <span className="font-normal normal-case">({body.length}/120 ideal, 1000 max)</span>
          </label>
          <textarea
            id="push-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={1000}
            rows={3}
            required
            placeholder="One short, warm sentence."
            className="w-full px-3 py-2.5 rounded-lg border border-moj-gold/40 bg-white focus:outline-none focus:ring-2 focus:ring-moj-gold focus:border-moj-gold resize-y"
            data-testid="push-body-input"
          />
        </div>

        <div>
          <label htmlFor="push-url" className="block text-xs font-semibold uppercase tracking-wide text-moj-charcoal/70 mb-1">
            Tap-through destination
          </label>
          <input
            id="push-url"
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="/live"
            className="w-full px-3 py-2.5 rounded-lg border border-moj-gold/40 bg-white focus:outline-none focus:ring-2 focus:ring-moj-gold focus:border-moj-gold"
            data-testid="push-url-input"
          />
          <p className="text-[11px] text-moj-charcoal/60 mt-1">Must be a path on this site (starts with <code>/</code>).</p>
        </div>

        {/* Schedule for later — segmented toggle keeps the compose form
            compact. The datetime picker only renders when enabled. */}
        <div className="rounded-xl bg-moj-cream border border-moj-gold/30 p-3">
          <div className="flex items-center gap-2 flex-wrap" role="group" aria-label="Send timing">
            <button
              type="button"
              onClick={() => setScheduleMode(false)}
              aria-pressed={!scheduleMode}
              className={`flex-1 sm:flex-initial text-xs font-semibold px-3 py-1.5 rounded-full border transition touch-manipulation ${
                !scheduleMode
                  ? 'bg-moj-purple text-white border-moj-purple shadow'
                  : 'bg-white text-moj-charcoal/70 border-moj-gold/30 hover:bg-moj-cream-soft'
              }`}
              data-testid="push-send-now-tab"
            >
              <Send size={12} className="inline mr-1"/> Send now
            </button>
            <button
              type="button"
              onClick={() => setScheduleMode(true)}
              aria-pressed={scheduleMode}
              className={`flex-1 sm:flex-initial text-xs font-semibold px-3 py-1.5 rounded-full border transition touch-manipulation ${
                scheduleMode
                  ? 'bg-moj-purple text-white border-moj-purple shadow'
                  : 'bg-white text-moj-charcoal/70 border-moj-gold/30 hover:bg-moj-cream-soft'
              }`}
              data-testid="push-schedule-tab"
            >
              <Timer size={12} className="inline mr-1"/> Schedule for later
            </button>
          </div>
          {scheduleMode && (
            <div className="mt-3" data-testid="push-schedule-picker">
              <label htmlFor="push-scheduled-at" className="block text-xs font-semibold uppercase tracking-wide text-moj-charcoal/70 mb-1">
                Send at
              </label>
              <input
                id="push-scheduled-at"
                type="datetime-local"
                value={scheduledAtLocal}
                onChange={(e) => setScheduledAtLocal(e.target.value)}
                min={minScheduleLocal()}
                className="w-full sm:w-auto px-3 py-2.5 rounded-lg border border-moj-gold/40 bg-white focus:outline-none focus:ring-2 focus:ring-moj-gold focus:border-moj-gold"
                data-testid="push-scheduled-at-input"
              />
              <p className="text-[11px] text-moj-charcoal/60 mt-1">
                Dispatcher runs every 15 minutes, so actual send is within <strong>15 min</strong> of the chosen time.
                {!scheduleFutureEnough && <span className="text-red-700"> · Pick a time at least 1 minute from now.</span>}
              </p>
            </div>
          )}
        </div>

        {/* Preview */}
        {(trimmedTitle || trimmedBody) && (
          <div className="rounded-lg bg-moj-charcoal text-white p-3.5 flex gap-3" data-testid="push-preview">
            <img src="/icons/icon-192.png" alt="" className="w-10 h-10 rounded-md flex-shrink-0 bg-white/10" />
            <div className="min-w-0">
              <div className="text-[11px] text-white/60 uppercase tracking-wider mb-0.5">Ministry of Jesus · now</div>
              <div className="text-sm font-semibold leading-tight line-clamp-1">{trimmedTitle || 'Title appears here'}</div>
              <div className="text-xs text-white/80 mt-0.5 line-clamp-2">{trimmedBody || 'Message appears here'}</div>
            </div>
          </div>
        )}

        <div className="pt-2 flex items-center gap-3 flex-wrap">
          <button
            type="submit"
            disabled={!canSend}
            className="btn-primary disabled:opacity-50"
            data-testid="push-send-btn"
          >
            {scheduleMode
              ? (scheduleMut.isPending
                  ? <><Loader2 size={16} className="animate-spin"/> Scheduling…</>
                  : <><Timer size={16}/> Schedule push</>)
              : (broadcast.isPending
                  ? <><Loader2 size={16} className="animate-spin"/> Sending…</>
                  : <><Send size={16}/> Send to {stats?.total ?? 0} device{(stats?.total ?? 0) === 1 ? '' : 's'}</>)
            }
          </button>
          <span className="text-xs text-moj-charcoal/60">
            {scheduleMode
              ? 'Scheduled pushes appear below and can be cancelled before send.'
              : "You'll be asked to confirm before it goes out."}
          </span>
        </div>
      </form>

      {/* Confirm dialog */}
      {confirming && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="push-confirm-title"
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="push-confirm-dialog"
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <h3 id="push-confirm-title" className="font-serif text-xl text-moj-purple-deep flex items-center gap-2">
              <Bell size={20}/> Send this notification?
            </h3>
            <p className="text-sm text-moj-charcoal/80 mt-2">
              This will push to <span className="font-semibold">{stats?.total ?? 0}</span> subscribed device{(stats?.total ?? 0) === 1 ? '' : 's'}.
              Members will see it on their lock screen.
            </p>
            <div className="mt-4 p-3 rounded-lg bg-moj-cream border border-moj-gold/30 text-sm">
              <div className="font-semibold text-moj-purple-deep">{trimmedTitle}</div>
              <div className="text-moj-charcoal/80 mt-1">{trimmedBody}</div>
              <div className="text-[11px] text-moj-charcoal/60 mt-2">→ opens <code>{url || '/'}</code></div>
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={broadcast.isPending}
                className="btn-outline"
                data-testid="push-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSend}
                disabled={broadcast.isPending}
                className="btn-primary disabled:opacity-60"
                data-testid="push-confirm-btn"
              >
                {broadcast.isPending ? <><Loader2 size={16} className="animate-spin"/> Sending…</> : <><CheckCircle2 size={16}/> Yes, send now</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scheduled drafts awaiting their send window */}
      {pendingDrafts.length > 0 && (
        <div className="card-moj p-5" data-testid="push-scheduled-panel">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-serif text-lg text-moj-purple-deep flex items-center gap-2">
                <Timer size={18} className="text-moj-gold-deep"/> Scheduled drafts
              </h3>
              <p className="text-xs text-moj-charcoal/70 mt-0.5">
                These will go out automatically within 15 minutes of their chosen time. Cancel any one before it sends.
              </p>
            </div>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800">
              {pendingDrafts.length} pending
            </span>
          </div>
          <ul className="mt-4 divide-y divide-moj-gold/20" data-testid="push-scheduled-list">
            {pendingDrafts.map((d) => (
              <li key={d.id} className="py-3 flex gap-3 items-start" data-testid={`push-scheduled-row-${d.id}`}>
                <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Calendar size={16}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-moj-purple-deep leading-tight line-clamp-1">{d.title}</div>
                  <div className="text-xs text-moj-charcoal/80 mt-0.5 line-clamp-2">{d.body}</div>
                  <div className="flex items-center gap-3 text-[11px] text-moj-charcoal/60 mt-1.5 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <Clock size={10}/>
                      {new Date(d.scheduled_at).toLocaleString('en-IE', { dateStyle: 'medium', timeStyle: 'short' })}
                    </span>
                    {d.url && d.url !== '/' && <span>→ <code className="text-[10px]">{d.url}</code></span>}
                    {d.actor?.name && <span>by {d.actor.name}</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Cancel this scheduled push "${d.title}"?`)) cancelDraft.mutate(d.id);
                  }}
                  disabled={cancelDraft.isPending}
                  className="text-xs font-semibold px-2.5 py-1 rounded-full border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 inline-flex items-center gap-1 touch-manipulation disabled:opacity-60"
                  data-testid={`push-scheduled-cancel-${d.id}`}
                >
                  <Trash2 size={10}/> Cancel
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Push history — newest 20 events across all channels */}
      <div className="card-moj p-5" data-testid="push-history-panel">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-serif text-lg text-moj-purple-deep flex items-center gap-2">
              <History size={18} className="text-moj-gold-deep"/> Recent broadcasts
            </h3>
            <p className="text-xs text-moj-charcoal/70 mt-0.5">
              Last {Math.min(20, historyData?.total ?? 0)} of {historyData?.total ?? 0} push events — manual sends, welcome pings and scheduled reminders.
            </p>
          </div>
          <div className="hidden sm:flex gap-1.5 text-[11px] text-moj-charcoal/60">
            <KindChip kind="manual"/>
            <KindChip kind="welcome"/>
            <KindChip kind="scheduled"/>
          </div>
        </div>

        {historyLoading ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-moj-charcoal/60">
            <Loader2 size={14} className="animate-spin"/> Loading history…
          </div>
        ) : (historyData?.items?.length ?? 0) === 0 ? (
          <div className="mt-4 py-8 text-center text-sm text-moj-charcoal/60" data-testid="push-history-empty">
            No broadcasts yet. Compose and send your first one above — it'll appear here.
          </div>
        ) : (
          <ul className="mt-4 divide-y divide-moj-gold/20" data-testid="push-history-list">
            {historyData.items.map((item, idx) => (
              <HistoryRow key={`${item.created_at}-${idx}`} item={item} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


// ---------- Sub-components ----------
const KIND_META = {
  manual:    { icon: Megaphone,    label: 'Manual',    cls: 'bg-moj-purple/10 text-moj-purple-deep border-moj-purple/30' },
  welcome:   { icon: Sparkles,     label: 'Welcome',   cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  scheduled: { icon: CalendarClock, label: 'Scheduled', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  prayer:    { icon: HandHeart,    label: 'Prayer',    cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};


function KindChip({ kind }) {
  const meta = KIND_META[kind] || KIND_META.manual;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${meta.cls}`}>
      <Icon size={10}/> {meta.label}
    </span>
  );
}


function formatRelativeTime(iso) {
  // Compact, human relative time — "2m ago", "3h ago", "yesterday", "Mon 10:15".
  // We prefer a stable short format in English since the admin UI is EN-only.
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const now = new Date();
  const diffMs = now - then;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: now.getFullYear() === then.getFullYear() ? undefined : 'numeric' });
}


function HistoryRow({ item }) {
  const stats = item.stats || {};
  const clicks = Number(item.clicks || 0);
  const deliveryRate = stats.attempted > 0
    ? Math.round((stats.sent / stats.attempted) * 100)
    : null;
  // Click-through rate — only meaningful when we actually delivered something.
  const ctr = stats.sent > 0 ? Math.round((clicks / stats.sent) * 100) : null;

  // Choose the badge colour so the admin can glance and know what happened:
  // green = all delivered; amber = partial; red = everyone failed.
  let rateCls = 'bg-moj-cream text-moj-charcoal/70 border-moj-gold/30';
  if (deliveryRate !== null) {
    if (stats.sent === stats.attempted && stats.attempted > 0) rateCls = 'bg-emerald-50 text-emerald-800 border-emerald-200';
    else if (stats.sent === 0 && stats.attempted > 0)          rateCls = 'bg-red-50 text-red-700 border-red-200';
    else                                                       rateCls = 'bg-amber-50 text-amber-800 border-amber-200';
  }

  return (
    <li
      className="py-3 flex gap-3 items-start"
      data-testid={`push-history-row-${item.kind}`}
    >
      <div className="flex-shrink-0 mt-0.5">
        <KindChip kind={item.kind} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <div className="font-semibold text-sm text-moj-purple-deep leading-tight line-clamp-1">{item.title}</div>
            <div className="text-xs text-moj-charcoal/80 mt-0.5 line-clamp-2">{item.body}</div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${rateCls}`}>
              {stats.attempted > 0
                ? `${stats.sent}/${stats.attempted} delivered`
                : 'no subscribers'}
            </span>
            {stats.sent > 0 && (
              <span
                className="text-[11px] font-semibold px-2 py-0.5 rounded-full border border-moj-purple/30 bg-moj-purple/5 text-moj-purple-deep whitespace-nowrap"
                title={ctr !== null ? `${ctr}% click-through rate` : ''}
                data-testid="push-history-clicks"
              >
                👆 {clicks}{ctr !== null ? ` · ${ctr}%` : ''}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-moj-charcoal/60 mt-1.5 flex-wrap">
          <span className="inline-flex items-center gap-1"><Clock size={10}/> {formatRelativeTime(item.created_at)}</span>
          {item.url && item.url !== '/' && (
            <span className="truncate">→ <code className="text-[10px]">{item.url}</code></span>
          )}
          {item.actor?.name && (
            <span className="truncate">by {item.actor.name}</span>
          )}
          {stats.staleRemoved > 0 && (
            <span className="text-amber-700">· cleaned {stats.staleRemoved} stale</span>
          )}
        </div>
      </div>
    </li>
  );
}


function StatCard({ icon, label, value, testid }) {
  return (
    <div className="rounded-xl bg-moj-cream border border-moj-gold/30 p-3 flex items-center gap-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg bg-moj-purple-deep text-moj-gold flex items-center justify-center">{icon}</div>
      <div>
        <div className="text-xs text-moj-charcoal/70 uppercase tracking-wide">{label}</div>
        <div className="text-xl font-serif text-moj-purple-deep leading-none">{value}</div>
      </div>
    </div>
  );
}
